import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    // Wijn.com 29-9-2026: de video pas laden als hij (bijna) in beeld komt. Een verborgen kopie of een
    // tegel die pas na vegen in beeld komt laadde anders meteen 2,7 MB, ook op de telefoon.
    if (!('IntersectionObserver' in window)) {
      this.#laad();
      return;
    }
    this.#waarnemer?.disconnect();
    this.#waarnemer = new IntersectionObserver(
      (ingangen) => {
        if (!ingangen.some((ingang) => ingang.isIntersecting)) return;
        this.#waarnemer?.disconnect();
        this.#waarnemer = null;
        this.#laad();
      },
      { rootMargin: '200px' }
    );
    this.#waarnemer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#waarnemer?.disconnect();
    this.#waarnemer = null;
  }

  /** @type {IntersectionObserver | null} */
  #waarnemer = null;

  #geladen = false;

  #laad() {
    if (this.#geladen) return;
    this.#geladen = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Wijn.com 31-10-2026: op de telefoon (Safari) start een video die later geladen wordt niet altijd
    // vanzelf en toont hij een afspeelknop. Daarom zelf starten, stil en zonder knop (baas: "automatisch
    // afspelen"). Lukt het niet (bijvoorbeeld energiebesparing aan), dan blijft het stilstaande beeld staan.
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('disablepictureinpicture', '');
    const speel = () => {
      const belofte = videoElement.play();
      if (belofte && typeof belofte.catch === 'function') belofte.catch(() => {});
    };
    videoElement.addEventListener('canplay', speel, { once: true });
    speel();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
